import { useCallback, useEffect, useMemo, useState } from "react";
import type { GitReadiness, MachineSummary, Project } from "@racler/protocol";
import { errorText } from "../lib/api.ts";
import { projectNameError } from "../lib/folderPath.ts";
import {
  computeDerivedLocation,
  deriveFolderNameFromRepoUrl,
  validateAdvancedSubdir,
} from "../lib/projectCreation.ts";
import type { ProjectAdmin } from "../lib/useProjectAdmin.ts";
import { FolderBrowser } from "./FolderBrowser.tsx";
import { GitSetupDialog } from "./GitSetupDialog.tsx";
import { needsGitNotice } from "../lib/gitSetup.ts";
import { useGitSetupApi } from "../lib/useGitSetupApi.ts";
import { FolderOpen, GitBranch, Plus } from "lucide-react";

export type ProjectCreationMode = "create" | "clone" | "add";

/**
 * Shown after a Project was created/added while Git is not READY. It never blocks the Project: Cancel (or finishing
 * setup) simply opens it, and only Agent Autonomous stays unavailable until readiness is READY.
 */
function ProjectGitNotice({
  machineId,
  project,
  readiness,
  onDone,
}: {
  machineId: string | null;
  project: Project;
  readiness: GitReadiness;
  onDone: (project: Project) => void;
}) {
  const api = useGitSetupApi(machineId ?? undefined, project.id);
  if (!api) return null;
  return (
    <GitSetupDialog api={api} initial={readiness} onReady={() => onDone(project)} onClose={() => onDone(project)} />
  );
}

/**
 * Create a Project or add an existing folder as a Project, INSIDE an approved Workspace:
 *   Machine → Workspace → (browse folders | Project name) → create / register → open.
 * Workspaces are approved on the machine itself (`racler workspace add`); this form cannot approve or name one, and it
 * can never name a path outside the chosen Workspace.
 */
export function AddProject({
  admin,
  initialMode = "create",
  onDone,
  onClose,
  machines,
  onSelectMachine,
}: {
  admin: ProjectAdmin;
  initialMode?: ProjectCreationMode;
  onDone: (project: Project) => void;
  onClose?: () => void;
  machines?: MachineSummary[];
  onSelectMachine?: (machineId: string) => void;
}) {
  const workspaces = useMemo(() => admin.workspaces?.filter((w) => w.browsable) ?? null, [admin.workspaces]);
  const [mode, setMode] = useState<ProjectCreationMode>(initialMode);
  const [workspaceId, setWorkspaceId] = useState(workspaces?.[0]?.id ?? "");

  // Create state
  const [name, setName] = useState("");
  const [createAdvanced, setCreateAdvanced] = useState(false);
  const [createSubdir, setCreateSubdir] = useState("");

  // Clone state
  const [repoUrl, setRepoUrl] = useState("");
  const [cloneFolderName, setCloneFolderName] = useState("");
  const [folderNameEdited, setFolderNameEdited] = useState(false);
  const [cloneAdvanced, setCloneAdvanced] = useState(false);
  const [cloneSubdir, setCloneSubdir] = useState("");

  // Add existing state
  const [addPath, setAddPath] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gitNotice, setGitNotice] = useState<{ project: Project; readiness: GitReadiness } | null>(null);

  useEffect(() => {
    if (workspaces && workspaces.length > 0 && !workspaces.some((w) => w.id === workspaceId)) {
      setWorkspaceId(workspaces[0]!.id);
      setAddPath("");
    }
  }, [workspaces, workspaceId]);

  const { browse } = admin;
  const browseWorkspace = useCallback((p: string) => browse(workspaceId, p), [browse, workspaceId]);

  const workspace = workspaces?.find((w) => w.id === workspaceId);

  const handleRepoUrlChange = (val: string) => {
    setRepoUrl(val);
    setError(null);
    if (!folderNameEdited) {
      const derived = deriveFolderNameFromRepoUrl(val);
      if (derived) setCloneFolderName(derived);
    }
  };

  const handleCloneFolderChange = (val: string) => {
    setCloneFolderName(val);
    setFolderNameEdited(true);
    setError(null);
  };

  const createSubdirError = createAdvanced ? validateAdvancedSubdir(createSubdir) : null;
  const cloneSubdirError = cloneAdvanced ? validateAdvancedSubdir(cloneSubdir) : null;
  const createNameError = name.trim() !== "" ? projectNameError(name.trim()) : null;
  const cloneNameError = cloneFolderName.trim() !== "" ? projectNameError(cloneFolderName.trim()) : null;

  const canSubmit = useMemo(() => {
    if (busy || !workspace) return false;
    if (mode === "create") {
      return name.trim() !== "" && !createNameError && !createSubdirError;
    }
    if (mode === "clone") {
      return (
        repoUrl.trim() !== "" &&
        cloneFolderName.trim() !== "" &&
        !cloneNameError &&
        !cloneSubdirError
      );
    }
    if (mode === "add") {
      return addPath !== "";
    }
    return false;
  }, [
    busy,
    workspace,
    mode,
    name,
    createNameError,
    createSubdirError,
    repoUrl,
    cloneFolderName,
    cloneNameError,
    cloneSubdirError,
    addPath,
  ]);

  const submit = async () => {
    if (!canSubmit || !workspace) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === "create") {
        const parentPath = createAdvanced ? createSubdir.trim() : "";
        const { project, git } = await admin.create(workspaceId, parentPath, name.trim());
        if (needsGitNotice(git)) setGitNotice({ project, readiness: git });
        else onDone(project);
      } else if (mode === "clone") {
        const parentPath = cloneAdvanced ? cloneSubdir.trim() : "";
        const { project, git } = await admin.clone(
          workspaceId,
          parentPath,
          cloneFolderName.trim(),
          repoUrl.trim(),
        );
        if (needsGitNotice(git)) setGitNotice({ project, readiness: git });
        else onDone(project);
      } else if (mode === "add") {
        const leaf = addPath.split("/").filter(Boolean).pop();
        const { project, git } = await admin.add(
          workspaceId,
          addPath,
          leaf || undefined,
        );
        if (needsGitNotice(git)) setGitNotice({ project, readiness: git });
        else onDone(project);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const previewLocation = () => {
    if (!workspace) return "";
    if (mode === "create") {
      const sub = createAdvanced ? createSubdir : "";
      return computeDerivedLocation(workspace.path, sub, name || "<project-name>");
    }
    if (mode === "clone") {
      const sub = cloneAdvanced ? cloneSubdir : "";
      return computeDerivedLocation(workspace.path, sub, cloneFolderName || "<folder-name>");
    }
    if (mode === "add") {
      return addPath ? `${workspace.path}/${addPath}` : workspace.path;
    }
    return "";
  };

  if (workspaces === null) return <p className="muted small">Loading workspaces…</p>;
  if (workspaces.length === 0) {
    return (
      <div className="card-plain" data-testid="no-workspaces">
        <p>No Workspace is approved on this machine yet.</p>
        <p className="muted small">
          For safety only you, on that computer, can approve where racler.dev may create or open Projects. A
          Workspace is a parent folder such as <code>~/Development</code>:
        </p>
        <pre className="code-block">racler workspace add ~/Development</pre>
        {onClose && (
          <div className="row end" style={{ marginTop: 12 }}>
            <button type="button" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <form
      className="add-project"
      data-testid="add-project"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="seg new-project-modes" role="tablist">
        <button
          type="button"
          key="create"
          className={mode === "create" ? "active" : ""}
          onClick={() => {
            setMode("create");
            setError(null);
          }}
          data-testid="mode-create"
        >
          <Plus size={13} aria-hidden /> Create
        </button>
        <button
          type="button"
          key="clone"
          className={mode === "clone" ? "active" : ""}
          onClick={() => {
            setMode("clone");
            setError(null);
          }}
          data-testid="mode-clone"
        >
          <GitBranch size={13} aria-hidden /> Clone Repository
        </button>
        <button
          type="button"
          key="add"
          className={mode === "add" ? "active" : ""}
          onClick={() => {
            setMode("add");
            setError(null);
          }}
          data-testid="mode-add"
        >
          <FolderOpen size={13} aria-hidden /> Add Existing
        </button>
      </div>

      <p className="muted small new-project-subtitle">
        {mode === "create" && "Create a new local folder and Project."}
        {mode === "clone" && "Clone an existing Git repository into a Workspace."}
        {mode === "add" && "Register an existing local folder."}
      </p>

      {machines && machines.length > 1 && (
        <label>
          Machine
          <select
            value={admin.machineId ?? ""}
            onChange={(e) => onSelectMachine?.(e.target.value)}
            data-testid="ws-machine"
          >
            {machines.map((m) => (
              <option key={m.id} value={m.id} disabled={!m.online}>
                {m.name} {!m.online ? "(offline)" : ""}
              </option>
            ))}
          </select>
        </label>
      )}

      <label>
        Workspace
        <select
          value={workspaceId}
          onChange={(e) => {
            setWorkspaceId(e.target.value);
            setAddPath("");
          }}
          data-testid="workspace-select-add"
        >
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name} — {w.path}
            </option>
          ))}
        </select>
      </label>
      {/* Mode 1: CREATE */}
      {mode === "create" && (
        <>
          <label>
            Project name
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="my-app"
              maxLength={100}
              data-testid="project-name"
              autoFocus
            />
          </label>

          <div className="location-preview-row">
            <span className="muted small">Location:</span>{" "}
            <code>{previewLocation()}</code>
          </div>

          <div className="advanced-location-section">
            <button
              type="button"
              className="advanced-location-toggle link-btn small muted"
              onClick={() => setCreateAdvanced(!createAdvanced)}
              data-testid="advanced-location-toggle"
            >
              {createAdvanced ? "▾ Hide advanced location" : "▸ Advanced location..."}
            </button>
            {createAdvanced && (
              <div className="advanced-location-fields">
                <label>
                  Parent subdirectory (optional)
                  <input
                    value={createSubdir}
                    onChange={(e) => setCreateSubdir(e.target.value)}
                    placeholder="e.g. customers/acme"
                    data-testid="advanced-location-input"
                  />
                </label>
                <p className="muted small">
                  Relative parent folder inside <code>{workspace?.path}</code>. Traversal (../) is rejected.
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Mode 2: CLONE REPOSITORY */}
      {mode === "clone" && (
        <>
          <label>
            Repository URL
            <input
              value={repoUrl}
              onChange={(e) => handleRepoUrlChange(e.target.value)}
              placeholder="git@github.com:owner/repository.git"
              data-testid="clone-repo-url"
              autoFocus
            />
          </label>

          <label>
            Folder name
            <input
              value={cloneFolderName}
              onChange={(e) => handleCloneFolderChange(e.target.value)}
              placeholder="repository"
              maxLength={100}
              data-testid="clone-folder-name"
            />
          </label>

          <div className="location-preview-row">
            <span className="muted small">Destination:</span>{" "}
            <code>{previewLocation()}</code>
          </div>

          <div className="advanced-location-section">
            <button
              type="button"
              className="advanced-location-toggle link-btn small muted"
              onClick={() => setCloneAdvanced(!cloneAdvanced)}
              data-testid="advanced-location-toggle"
            >
              {cloneAdvanced ? "▾ Hide advanced location" : "▸ Advanced location..."}
            </button>
            {cloneAdvanced && (
              <div className="advanced-location-fields">
                <label>
                  Parent subdirectory (optional)
                  <input
                    value={cloneSubdir}
                    onChange={(e) => setCloneSubdir(e.target.value)}
                    placeholder="e.g. customers/acme"
                    data-testid="advanced-location-input"
                  />
                </label>
                <p className="muted small">
                  Relative parent folder inside <code>{workspace?.path}</code>. Traversal (../) is rejected.
                </p>
              </div>
            )}
          </div>
        </>
      )}
      {/* Mode 3: ADD EXISTING */}
      {mode === "add" && (
        <>
          {workspace && (
            <div className="add-folder-browser-card">
              <p className="muted small">Choose folder inside {workspace.name}:</p>
              <FolderBrowser
                workspaceName={workspace.name}
                browse={browseWorkspace}
                path={addPath}
                onPath={(p) => {
                  setAddPath(p);
                  setError(null);
                }}
              />
            </div>
          )}

          {addPath && (
            <div className="location-preview-row">
              <span className="muted small">Project path:</span>{" "}
              <code>{previewLocation()}</code>
            </div>
          )}
        </>
      )}

      {((mode === "create" && (createNameError || createSubdirError)) ||
        (mode === "clone" && (cloneNameError || cloneSubdirError)) ||
        error) && (
        <p className="msg-error" role="alert" data-testid="add-error">
          {mode === "create"
            ? (createNameError ?? createSubdirError ?? error)
            : mode === "clone"
              ? (cloneNameError ?? cloneSubdirError ?? error)
              : error}
        </p>
      )}

      <div className="row end new-project-footer-actions">
        {onClose && (
          <button type="button" onClick={onClose} data-testid="add-project-cancel">
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="primary"
          disabled={!canSubmit}
          data-testid="add-project-submit"
        >
          {busy
            ? "Working…"
            : mode === "create"
              ? "Create Project"
              : mode === "clone"
                ? "Clone Repository"
                : "Add Project"}
        </button>
      </div>

      {gitNotice && (
        <ProjectGitNotice
          machineId={admin.machineId}
          project={gitNotice.project}
          readiness={gitNotice.readiness}
          onDone={onDone}
        />
      )}
    </form>
  );
}
